import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import ProseLogo from "../components/ProseLogo";

export default function CaseStudy() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <DisclaimerBanner />
      <nav className="border-b border-slate-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="cs-logo">
            <ProseLogo size={40} />
          </Link>
          <Link to="/" className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 hover:text-slate-900">
            ← Back to home
          </Link>
        </div>
      </nav>

      <main className="max-w-[820px] mx-auto px-6 py-16 md:py-24" data-testid="case-study-page">
        <div className="label-tiny mb-4">R&amp;D Case Study</div>
        <h1 className="font-serif-display font-light text-4xl md:text-6xl leading-[1.02] tracking-tight">
          Why we built <em className="not-italic font-semibold">ProSe.Claims</em>.
        </h1>
        <p className="mt-8 text-lg text-slate-600 leading-relaxed">
          ProSe.Claims began as an internal MisherTech research project investigating whether structured intake, evidence
          organization, and modern language models could measurably reduce the effort required for a self-represented
          litigant to prepare a civil complaint that is coherent, complete, and ready for a licensed attorney&apos;s review.
        </p>

        <div className="mt-14 space-y-12">
          <Section n="01" title="The problem we heard, again and again">
            <p>
              Consumers routinely told us the same story: legal help was too expensive, too slow, or too skeptical.
              People with valid grievances were repeating their facts to intake staff, family members, and rejected
              attorneys, without ever producing a document a court could actually read.
            </p>
          </Section>

          <Section n="02" title="What we tested">
            <p>
              We evaluated three questions. Can a step-by-step questionnaire capture the elements of a US civil complaint
              without imitating legal advice? Can an evidence organizer produce reliable exhibit lists? Can a language
              model rewrite plaintext facts into neutral, court-ready sections without inventing statutes or citations?
            </p>
          </Section>

          <Section n="03" title="What we learned">
            <p>
              Structured intake reduced the time to a filable-quality draft dramatically, on the order of days into
              minutes for a well-organized user. Guardrails on the model, no bare statutes, no invented facts, kept
              outputs safe. The remaining variance was human: how the user chose to describe the harm.
            </p>
          </Section>

          <Section n="04" title="Where we are now">
            <p>
              During the R&amp;D phase, ProSe.Claims is free to use. It is not a law firm and does not provide legal
              advice. Every generated draft is labeled as requiring independent review. Our public goal is to publish the
              methodology and the anonymized outcomes so that self-represented litigants everywhere benefit, even if they
              never use our tool.
            </p>
          </Section>
        </div>

        <div className="mt-16 border-t border-slate-200 pt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-900 hover:gap-3 transition-all"
            data-testid="cs-back-cta"
          >
            Try ProSe.Claims <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
            data-testid="cs-contact-cta"
          >
            Talk to the research team
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="grid grid-cols-12 gap-4 items-start">
      <div className="col-span-2 font-mono-tech text-xs text-slate-500 pt-1">{n}</div>
      <div className="col-span-10">
        <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 leading-tight mb-3">{title}</h2>
        <div className="text-slate-700 text-base leading-relaxed space-y-3">{children}</div>
      </div>
    </section>
  );
}
